import { createContext, useState, useCallback, useContext, useMemo } from 'react';

import { AuthModal } from '../../components/modals/AuthModal';
import { HowToEarnPointsModal } from '../../components/modals/HowToEarnPointsModal';
import {
    ModalContextValue,
    ModalProviderProps,
    AuthModalOptions,
    HowToEarnPointsModalOptions,
} from './ModalContext.types';

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

export const ModalProvider = ({ children }: ModalProviderProps) => {
    // Модальное окно авторизации
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [authModalOptions, setAuthModalOptions] = useState<AuthModalOptions>({});

    const openAuthModal = useCallback((options: AuthModalOptions = {}) => {
        setAuthModalOptions(options);
        setAuthModalOpen(true);
    }, []);
    const closeAuthModal = useCallback(() => setAuthModalOpen(false), []);

    // Модальное окно "Как повысить рейтинг"
    const [howToEarnPointsModalOpen, setHowToEarnPointsModalOpen] = useState(false);

    const openHowToEarnPointsModal = useCallback((_options: HowToEarnPointsModalOptions = {}) => {
        setHowToEarnPointsModalOpen(true);
    }, []);
    const closeHowToEarnPointsModal = useCallback(() => setHowToEarnPointsModalOpen(false), []);

    const value: ModalContextValue = useMemo(
        () => ({
            auth: {
                isOpen: authModalOpen,
                open: openAuthModal,
                close: closeAuthModal,
            },
            howToEarnPoints: {
                isOpen: howToEarnPointsModalOpen,
                open: openHowToEarnPointsModal,
                close: closeHowToEarnPointsModal,
            },
        }),
        [
            authModalOpen,
            openAuthModal,
            closeAuthModal,
            howToEarnPointsModalOpen,
            openHowToEarnPointsModal,
            closeHowToEarnPointsModal,
        ],
    );

    return (
        <ModalContext.Provider value={value}>
            {children}

            <AuthModal
                retpath={authModalOptions.retpath}
                isOpen={authModalOpen}
                onClose={value.auth.close}
                onCloseFunction={authModalOptions.onClose}
            />

            <HowToEarnPointsModal isOpen={howToEarnPointsModalOpen} onClose={value.howToEarnPoints.close} />
        </ModalContext.Provider>
    );
};

export const useModal = (): ModalContextValue => {
    const context = useContext(ModalContext);

    if (context === undefined) {
        throw new Error('useModal must be used within a ModalProvider');
    }

    return context;
};
